import { DtoCubeDataResponse } from "@/types/dto-types";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  QuadrantAxis,
  QuadrantTotal,
} from "../../../../ParticipationFlow/components/quadrant";
import { BarChart } from "../../../components/BarChart";
import { CATEGORY_ICONS } from "../../../components/ChallengeRow";
import { CockpitSidePanel } from "../../../components/CockpitSidePanel";
import { DoubleToggle } from "../../../components/DoubleToggle";
import { MenuButton } from "../../../components/MenuButton";
import { ScoreBenchmarkOverview } from "../../../components/ScoreBenchmarkOverview";
import { Dropdown, DropdownItem } from "../../../components/Dropdown";
import { Cube } from "../../../components/Сube";
import { CubeSideName } from "../../../components/Сube/types";
import { Benchmark, SelectedFilters, useUrlState } from "../../../hooks";
import { useCategoryPageData } from "../../../hooks/useCategoryPageData";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { CalculationType, Category, CategoryNavItem } from "../../../types";
import {
  getBarChartData,
  getBarChartLabels,
  renderNumber,
} from "../../../utils";
import { SortOrder } from "../../../constants";
import "./style.scss";

export interface CategoryPageProps {
  category: CategoryNavItem;
  onHomeClick: () => void;
  benchmarks: Benchmark[];
  selectedBenchmark?: Benchmark;
  setSelectedBenchmark: (benchmark: Benchmark) => void;
  rawData?: DtoCubeDataResponse;
  currentTestId: string;
  selectedFilters: SelectedFilters | undefined;
  selectedCalculationType: CalculationType;
  setSelectedCalculationType: (calculationType: CalculationType) => void;
}

export const CategoryPage = ({
  category,
  benchmarks,
  onHomeClick,
  rawData,
  currentTestId,
  selectedFilters,
  setSelectedBenchmark,
  selectedBenchmark,
  selectedCalculationType,
  setSelectedCalculationType,
}: CategoryPageProps) => {
  const {
    updatePath,
    path: { cubeSide },
  } = useUrlState();

  const [selectedQuadrantAxis, setSelectedQuadrantAxis] = useState<
    "precise" | "clustered"
  >("precise");
  const [averageScoreItemsOpen, setAverageScoreItemsOpen] = useState(false);
  const [hoveredCubeSide, setHoveredCubeSide] = useState<CubeSideName>();
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortTriggerRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  const categoryPageData = useCategoryPageData({
    currentCategory: category as Category,
    rawData,
    currentTestId,
    selectedFilters,
    benchmarkTitle:
      selectedBenchmark?.id === "default"
        ? undefined
        : selectedBenchmark?.title,
  });

  const { t } = useTranslation();

  const filteredPageData = useMemo(() => {
    if (!cubeSide) {
      return categoryPageData;
    }

    return categoryPageData.cubeSidesData[cubeSide];
  }, [categoryPageData, cubeSide]);

  const handleCubeSideClick = useCallback(
    (side: CubeSideName) => {
      updatePath({ cubeSide: cubeSide === side ? undefined : side });
    },
    [cubeSide, updatePath]
  );

  const handleBackClick = useCallback(() => {
    if (cubeSide) {
      updatePath({ cubeSide: undefined });
    } else {
      onHomeClick();
    }
  }, [cubeSide, onHomeClick, updatePath]);

  const handleAverageScoreItemsClick = useCallback(() => {
    setAverageScoreItemsOpen(!averageScoreItemsOpen);
  }, [averageScoreItemsOpen]);

  const handleSortChange = useCallback((item: DropdownItem) => {
    setSortOrder(item.value as SortOrder);
    setIsSortDropdownOpen(false);
  }, []);

  const sortOptionLabels = {
    [SortOrder.ASC]: t(
      "frontend.futurebilityCockpit.averageScoreItems.sort.asc",
      "Score ascending"
    ),
    [SortOrder.DESC]: t(
      "frontend.futurebilityCockpit.averageScoreItems.sort.desc",
      "Score descending"
    ),
    [SortOrder.ORIGINAL]: t(
      "frontend.futurebilityCockpit.averageScoreItems.sort.original",
      "Order of participation"
    ),
  };

  const sortedItemizedAverageScoreData = useMemo(() => {
    const dataArray = cubeSide
      ? categoryPageData.cubeSidesData[cubeSide].itemizedAverageScoreData
      : categoryPageData.itemizedAverageScoreData;

    if (sortOrder === SortOrder.ORIGINAL) {
      return dataArray;
    }

    return [...dataArray].sort((a, b) =>
      sortOrder === SortOrder.ASC
        ? a.averageScore - b.averageScore
        : b.averageScore - a.averageScore
    );
  }, [categoryPageData, cubeSide, sortOrder]);

  const renderCategoryInfo = (currentCubeSide: CubeSideName) => {
    const percent =
      categoryPageData.cubeSidesData?.[currentCubeSide]?.[
        selectedCalculationType
      ].percentageValue ?? 0;
    const title =
      categoryPageData.cubeSidesData?.[currentCubeSide]?.title ?? "N/A";

    const isActive =
      hoveredCubeSide === currentCubeSide || cubeSide === currentCubeSide;

    return (
      <div
        className={`side-score ${isActive ? "active" : ""}`}
        onClick={() => handleCubeSideClick(currentCubeSide)}
      >
        <div className="title">{title}</div>
        <div className="value">
          {renderNumber(
            percent,
            selectedCalculationType === CalculationType.Benchmark
          )}
          %
        </div>
      </div>
    );
  };

  const cubeData = (
    Object.keys(categoryPageData.cubeSidesData ?? {}) as CubeSideName[]
  ).reduce(
    (acc, side) => ({
      ...acc,
      [side]:
        categoryPageData.cubeSidesData?.[side]?.[selectedCalculationType]
          .cubeSideFillArea,
    }),
    {} as Record<CubeSideName, number | undefined>
  );

  const renderCubeSidePanel = (hoveredCubeSide: CubeSideName) => {
    const data = categoryPageData.cubeSidesData?.[hoveredCubeSide];
    const shouldDisplay = !!cubeSide
      ? hoveredCubeSide !== cubeSide
      : hoveredCubeSide !== undefined;

    if (!shouldDisplay) return null;

    return (
      <CockpitSidePanel
        title={data?.title}
        score={data?.score}
        benchmark={data?.benchmark}
        difference={data?.scoreVsBenchmarkDifference}
        description={data?.description}
      />
    );
  };

  return (
    <div className="analytics-category-page">
      <div className="left">
        <section className="cube-overview">
          <section className="cube-section">
            <div className="category-view">
              <div className="cube-category-row">
                <MenuButton
                  variant="arrow-left"
                  text={
                    cubeSide
                      ? t("frontend.futurebilityCockpit.back", "Back")
                      : t("frontend.futurebilityCockpit.home", "Home")
                  }
                  onClick={handleBackClick}
                />
                <div className="cube-category-title">
                  {CATEGORY_ICONS[category]}{" "}
                  {t(
                    `frontend.futurebilityCockpit.categories.${category}.title`
                  )}
                </div>
              </div>
              <div className="extended-cube-container">
                <div className="row justify-center">
                  {renderCategoryInfo("2")}
                </div>
                <div className="row justify-center">
                  <div className="side-group left">
                    {renderCategoryInfo("5")}
                    {renderCategoryInfo("4")}
                  </div>
                  <div className="cube-wrapper">
                    <Cube
                      greyOutOtherSidesOnHover
                      values={cubeData}
                      onSideHover={setHoveredCubeSide}
                      onSideClick={handleCubeSideClick}
                      selectedSides={cubeSide ? [cubeSide] : []}
                    />
                  </div>
                  <div className="side-group right">
                    {renderCategoryInfo("6")}
                    {renderCategoryInfo("3")}
                  </div>
                </div>
                <div className="row justify-center">
                  {renderCategoryInfo("1")}
                </div>
                {hoveredCubeSide !== undefined &&
                  renderCubeSidePanel(hoveredCubeSide)}
              </div>
            </div>
          </section>
          <section className="cube-toggle">
            <DoubleToggle
              leftOption={{
                id: CalculationType.Benchmark,
                label: t(
                  "frontend.futurebilityCockpit.scoreType.benchmark",
                  "Benchmark"
                ),
              }}
              rightOption={{
                id: CalculationType.AverageScore,
                label: t(
                  "frontend.futurebilityCockpit.scoreType.corporateScore",
                  "Corporate Score"
                ),
              }}
              selectedOptionId={selectedCalculationType}
              onChange={optionId => {
                setSelectedCalculationType(optionId as CalculationType);
              }}
            />
          </section>
        </section>
      </div>
      <div className="right">
        {averageScoreItemsOpen ? (
          <div className="average-score-items">
            <div className="average-score-items-top">
              <div className="average-score-items-top-title">
                <div>
                  {t(
                    "frontend.futurebilityCockpit.averageScoreItems.title",
                    "Score Average"
                  )}
                </div>
                <div className="sort-dropdown-wrapper">
                  <div
                    className="sort-trigger"
                    ref={sortTriggerRef}
                    onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  >
                    {sortOptionLabels[sortOrder]}
                    <svg
                      width="10"
                      height="6"
                      viewBox="0 0 10 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        transform: isSortDropdownOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.1s",
                      }}
                    >
                      <path
                        d="M1 1L5 5L9 1"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  {isSortDropdownOpen && (
                    <Dropdown
                      items={[
                        SortOrder.ASC,
                        SortOrder.DESC,
                        SortOrder.ORIGINAL,
                      ].map(order => ({
                        id: order,
                        title: sortOptionLabels[order],
                        value: order,
                      }))}
                      isOpen={isSortDropdownOpen}
                      onClose={() => setIsSortDropdownOpen(false)}
                      triggerRef={sortTriggerRef}
                      onSelect={handleSortChange}
                    />
                  )}
                </div>
              </div>
              <div
                className="average-score-items-top-close"
                onClick={() => setAverageScoreItemsOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M16.75 0.75L0.75 16.75"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M0.75 0.75L16.75 16.75"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="average-score-items-overview">
              {getBarChartLabels(t).map(({ label }, index: number) => (
                <div key={index} className="average-score-items-overview-label">
                  {label}
                </div>
              ))}
            </div>
            <div className="average-score-items-overview-content">
              {sortedItemizedAverageScoreData.map((item, index) => (
                <div
                  key={index}
                  className="average-score-items-overview-content-item"
                >
                  <BarChart
                    showLabel={false}
                    bars={getBarChartData(
                      item.participationQuarterAreaItems,
                      t
                    )}
                  />
                  <div className="average-score-items-overview-content-label">
                    {item.questionTitle}{" "}
                    <span className="average-score-items-overview-content-value">
                      {renderNumber(item.averageScore, false)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <ScoreBenchmarkOverview
              benchmarks={benchmarks}
              score={filteredPageData.score}
              setSelectedBenchmark={setSelectedBenchmark}
              selectedBenchmark={selectedBenchmark}
              isCategoryPage
            />
            <section className="bar-chart-section">
              <BarChart
                bars={getBarChartData(
                  filteredPageData.participationQuarterAreaItems,
                  t
                )}
              />
              <MenuButton text="Items" onClick={handleAverageScoreItemsClick} />
            </section>
            <div className="quadrant-axis-title">
              {t(
                "frontend.futurebilityCockpit.quadrantAxisTitle",
                "Quality VS Importance"
              )}
            </div>
            <div className="quadrant-wrap-content">
              {selectedQuadrantAxis === "precise" ? (
                <QuadrantAxis
                  xTitle={t(
                    "frontend.futurebilityCockpit.quadrantAxis.xTitle",
                    "Importance"
                  )}
                  yTitle={t(
                    "frontend.futurebilityCockpit.quadrantAxis.yTitle",
                    "Quality"
                  )}
                  points={filteredPageData.qualityImportanceItems}
                  width={isMobile ? 300 : undefined}
                  height={isMobile ? 300 : undefined}
                />
              ) : (
                <QuadrantTotal
                  points={filteredPageData.qualityImportanceItems}
                  xLeftTitle={t(
                    "frontend.futurebilityCockpit.quadrantTotal.xLeftTitle",
                    "Low"
                  )}
                  xCenterTitle={t(
                    "frontend.futurebilityCockpit.quadrantTotal.xCenterTitle",
                    "Importance"
                  )}
                  xRightTitle={t(
                    "frontend.futurebilityCockpit.quadrantTotal.xRightTitle",
                    "High"
                  )}
                  yTopTitle={t(
                    "frontend.futurebilityCockpit.quadrantTotal.yTopTitle",
                    "High"
                  )}
                  yCenterTitle={t(
                    "frontend.futurebilityCockpit.quadrantTotal.yCenterTitle",
                    "Quality"
                  )}
                  yBottomTitle={t(
                    "frontend.futurebilityCockpit.quadrantTotal.yBottomTitle",
                    "Low"
                  )}
                  width={isMobile ? 325 : undefined}
                  height={isMobile ? 325 : undefined}
                />
              )}
            </div>
            <div className="quadrant-toggle-wrap">
              <DoubleToggle
                leftOption={{
                  id: "precise",
                  label: t(
                    "frontend.futurebilityCockpit.quadrantToggle.precise",
                    "Precise"
                  ),
                }}
                rightOption={{
                  id: "clustered",
                  label: t(
                    "frontend.futurebilityCockpit.quadrantToggle.clustered",
                    "Clustered"
                  ),
                }}
                selectedOptionId={selectedQuadrantAxis}
                onChange={optionId => {
                  setSelectedQuadrantAxis(optionId as "precise" | "clustered");
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
